/**
 * @fileoverview Reusable Privacy Policy content component.
 * Used by both the dialog modal and standalone page.
 * @module features/landing/components/PrivacyPolicyContent
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Privacy Policy content component.
 * Displays GDPR/RODO compliant privacy policy content.
 *
 * @component
 */
export function PrivacyPolicyContent(): ReactElement {
  const { t } = useTranslation('features-landing')

  return (
    <div className="space-y-6 text-sm text-muted-foreground">
      {/* Subtitle */}
      <p className="text-xs text-muted-foreground/70">
        {t('privacyPolicy.subtitle')}
      </p>
      <p className="text-xs text-muted-foreground/70">
        {t('privacyPolicy.lastUpdated')}
      </p>

      {/* Section 1 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.general.title')}
        </h3>
        <p>{t('privacyPolicy.sections.general.p1')}</p>
        <p>{t('privacyPolicy.sections.general.p2')}</p>
      </section>

      {/* Section 2 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.controller.title')}
        </h3>
        <p>{t('privacyPolicy.sections.controller.description')}</p>
        <div className="pl-4 space-y-1">
          <p className="font-medium text-foreground">Vladyslav Dudnyk</p>
          <p>{t('privacyPolicy.sections.controller.role')}</p>
          <p>Email: office@abyssblock.com</p>
        </div>
        <p>{t('privacyPolicy.sections.controller.note')}</p>
      </section>

      {/* Section 3 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.scope.title')}
        </h3>
        <p>{t('privacyPolicy.sections.scope.description')}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t('privacyPolicy.sections.scope.items.email')}</li>
          <li>{t('privacyPolicy.sections.scope.items.ip')}</li>
          <li>{t('privacyPolicy.sections.scope.items.technical')}</li>
          <li>{t('privacyPolicy.sections.scope.items.cookies')}</li>
        </ul>
        <p>{t('privacyPolicy.sections.scope.note')}</p>
      </section>

      {/* Section 4 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.purpose.title')}
        </h3>
        <p>{t('privacyPolicy.sections.purpose.description')}</p>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 font-medium text-foreground">
                  {t('privacyPolicy.sections.purpose.table.purpose')}
                </th>
                <th className="text-left p-2 font-medium text-foreground">
                  {t('privacyPolicy.sections.purpose.table.basis')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.inquiries.purpose')}</td>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.inquiries.basis')}</td>
              </tr>
              <tr>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.beta.purpose')}</td>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.beta.basis')}</td>
              </tr>
              <tr>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.analytics.purpose')}</td>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.analytics.basis')}</td>
              </tr>
              <tr>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.technical.purpose')}</td>
                <td className="p-2">{t('privacyPolicy.sections.purpose.table.rows.technical.basis')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 5 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.recipients.title')}
        </h3>
        <p>{t('privacyPolicy.sections.recipients.description')}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t('privacyPolicy.sections.recipients.items.hosting')}</li>
          <li>{t('privacyPolicy.sections.recipients.items.analytics')}</li>
          <li>{t('privacyPolicy.sections.recipients.items.email')}</li>
        </ul>
        <p>{t('privacyPolicy.sections.recipients.noSale')}</p>
        <p>{t('privacyPolicy.sections.recipients.international')}</p>
      </section>

      {/* Section 6 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.retention.title')}
        </h3>
        <p>{t('privacyPolicy.sections.retention.description')}</p>
        <p>{t('privacyPolicy.sections.retention.betaEmails')}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t('privacyPolicy.sections.retention.items.betaEnds')}</li>
          <li>{t('privacyPolicy.sections.retention.items.deletion')}</li>
          <li>{t('privacyPolicy.sections.retention.items.legalChange')}</li>
        </ul>
      </section>

      {/* Section 7 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.cookies.title')}
        </h3>
        <p>{t('privacyPolicy.sections.cookies.description')}</p>
        <p>{t('privacyPolicy.sections.cookies.types')}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><span className="font-medium text-foreground">{t('privacyPolicy.sections.cookies.necessary.name')}</span> – {t('privacyPolicy.sections.cookies.necessary.description')}</li>
          <li><span className="font-medium text-foreground">{t('privacyPolicy.sections.cookies.analytics.name')}</span> – {t('privacyPolicy.sections.cookies.analytics.description')}</li>
        </ul>
        <p>{t('privacyPolicy.sections.cookies.management')}</p>
      </section>

      {/* Section 8 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.rights.title')}
        </h3>
        <p>{t('privacyPolicy.sections.rights.description')}</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>{t('privacyPolicy.sections.rights.items.access')}</li>
          <li>{t('privacyPolicy.sections.rights.items.rectify')}</li>
          <li>{t('privacyPolicy.sections.rights.items.deletion')}</li>
          <li>{t('privacyPolicy.sections.rights.items.restrict')}</li>
          <li>{t('privacyPolicy.sections.rights.items.object')}</li>
          <li>{t('privacyPolicy.sections.rights.items.withdraw')}</li>
          <li>{t('privacyPolicy.sections.rights.items.complaint')}</li>
        </ul>
        <p>{t('privacyPolicy.sections.rights.contact')}</p>
      </section>

      {/* Section 9 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.security.title')}
        </h3>
        <p>{t('privacyPolicy.sections.security.description')}</p>
      </section>

      {/* Section 10 */}
      <section className="space-y-2">
        <h3 className="text-base font-medium text-foreground">
          {t('privacyPolicy.sections.changes.title')}
        </h3>
        <p>{t('privacyPolicy.sections.changes.description')}</p>
      </section>

      {/* Footer note */}
      <div className="pt-4 border-t border-border text-xs text-muted-foreground/70 italic">
        <p>{t('privacyPolicy.footerNote.line1')}</p>
        <p>{t('privacyPolicy.footerNote.line2')}</p>
      </div>
    </div>
  )
}
